import {Individual} from '../application-service.domain';
import {ApplicationIndividual} from '../application-parties.domain';

export interface ApplicationConfirmationDialogIndividual {
  id: string;
  firstName: string;
  lastName: string;
}

export interface ApplicationConfirmationDialogData {
  messages: {id: string, message: string}[];
  /** Individuals who will receive a privacy consent form on submission. */
  individuals?: ApplicationConfirmationDialogIndividual[];
}

export interface ApplicationConfirmationDialogResult {
  [id: string]: boolean;
  readyForSubmission: boolean;
}

/**
 * Consent signatories for wizards that have the "Individual Details" step, taken from the SAVED
 * ApplicationIndividual rows that step exposes. That is the only complete list: on those wizards the
 * applicant/guarantor step form values hold just the manually entered people, while KYB key people
 * and KYB-selected guarantors are carried as stakeholder ids and only ever materialise as individuals
 * server-side. It also matches who the backend actually dispatches a consent form to on submission -
 * every non-deleted individual on the application.
 */
export const savedIndividualsToConfirmationIndividuals = (
  savedIndividuals: Individual[] | undefined,
): ApplicationConfirmationDialogIndividual[] =>
  confirmationIndividuals(
    (savedIndividuals ?? []).map(individual => ({
      id: individual.id,
      deleted: individual.deleted === true,
      firstName: individual.GivenName,
      lastName: individual.SurName,
    })),
  );

/**
 * The same list from the API shape, for callers reading the digital-ID endpoint rather than the
 * wizard's saved rows.
 *
 * Keyed on `guid`, not `id`: the dialog id is a string and the consent-checkbox result is keyed by
 * it, whereas the API shape's `id` is the numeric row id. Passing `id` would still compile through
 * the template's index signature (AZ-1475).
 */
export const apiIndividualsToConfirmationIndividuals = (
  individuals: ApplicationIndividual[] | undefined,
): ApplicationConfirmationDialogIndividual[] =>
  confirmationIndividuals(
    (individuals ?? []).map(individual => ({
      id: individual.guid,
      deleted: individual.isDeleted === true,
      firstName: individual.givenName,
      lastName: individual.surname,
    })),
  );

const confirmationIndividuals = (
  individuals: { id?: string | null; deleted: boolean; firstName?: string | null; lastName?: string | null }[],
): ApplicationConfirmationDialogIndividual[] =>
  individuals
    .filter(individual => !individual.deleted)
    .map((individual, index) => ({
      id: individual.id ?? `individual-${index}`,
      firstName: individual.firstName ?? '',
      lastName: individual.lastName ?? '',
    }));
